import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  AlertCircle,
  CheckCircle,
  CalendarIcon,
  Download,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  AlertTriangle,
  Award
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay, isWithinInterval, differenceInDays } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { formatDateToString } from '@/lib/dateUtils';

type StaffTodo = Tables<'staff_todos'> & { category?: string };

interface StaffPerformanceReviewProps {
  isOpen: boolean;
  onClose: () => void;
  staffId: string;
  staffName: string;
  todos: StaffTodo[];
}

type DateRangeOption = '7days' | '30days' | '90days' | '180days' | '365days' | 'custom';

interface PerformanceMetrics {
  totalTasks: number;
  completedTasks: number;
  onTimeTasks: number;
  lateTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  cancelledTasks: number;
  successRate: number;
  averageDaysEarly: number;
  averageDaysLate: number;
  averageCompletionTime: number; // in days
  taskVelocity: number; // tasks per week
}

interface CategoryPerformance {
  category: string;
  total: number;
  completed: number;
  onTime: number;
  late: number;
  successRate: number;
  averageCompletionTime: number;
}

interface PriorityPerformance {
  priority: string;
  total: number;
  completed: number;
  onTime: number;
  late: number;
  successRate: number;
}

const TASK_CATEGORIES = [
  'General Admin',
  'Marketing',
  'HR & Staff Management',
  'Payroll – Staff',
  'Payroll – Doctor',
  'Accounts',
  'Finance',
  'Operations',
  'Compliance',
  'Events – Internal',
  'Events – Education',
  'Events – External',
  'Management Planning',
  'Management Reports'
];

const StaffPerformanceReview: React.FC<StaffPerformanceReviewProps> = ({
  isOpen,
  onClose,
  staffId,
  staffName,
  todos
}) => {
  const { toast } = useToast();
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>('30days');
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined
  });
  const [chartViewMode, setChartViewMode] = useState<'count' | 'estimated' | 'actual'>('count');
  const [selectedTaskType, setSelectedTaskType] = useState<'Routine' | 'Ad-hoc' | null>(null);

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    const today = endOfDay(new Date());
    let from: Date;
    let to: Date = today;

    switch (dateRangeOption) {
      case '7days':
        from = startOfDay(subDays(today, 7));
        break;
      case '30days':
        from = startOfDay(subDays(today, 30));
        break;
      case '90days':
        from = startOfDay(subDays(today, 90));
        break;
      case '180days':
        from = startOfDay(subDays(today, 180));
        break;
      case '365days':
        from = startOfDay(subDays(today, 365));
        break;
      case 'custom':
        from = customDateRange.from ? startOfDay(customDateRange.from) : startOfDay(subDays(today, 30));
        to = customDateRange.to ? endOfDay(customDateRange.to) : today;
        break;
      default:
        from = startOfDay(subDays(today, 30));
    }

    return { from, to };
  }, [dateRangeOption, customDateRange]);

  // Filter todos for this staff member within the date range
  const filteredTodos = useMemo(() => {
    return todos.filter(todo => {
      if (todo.staff_id !== staffId) return false;
      
      // Check if task falls within date range
      const taskDate = new Date(todo.created_at);
      if (!isWithinInterval(taskDate, { start: dateRange.from, end: dateRange.to })) {
        return false;
      }
      
      return true;
    });
  }, [todos, staffId, dateRange]);

  // Calculate performance metrics
  const metrics = useMemo((): PerformanceMetrics => {
    const result: PerformanceMetrics = {
      totalTasks: 0,
      completedTasks: 0,
      onTimeTasks: 0,
      lateTasks: 0,
      pendingTasks: 0,
      overdueTasks: 0,
      cancelledTasks: 0,
      successRate: 0,
      averageDaysEarly: 0,
      averageDaysLate: 0,
      averageCompletionTime: 0,
      taskVelocity: 0
    };

    let totalDaysEarly = 0;
    let totalDaysLate = 0;
    let totalCompletionTime = 0;

    filteredTodos.forEach(todo => {
      result.totalTasks++;

      if (todo.status === 'completed') {
        result.completedTasks++;
        
        // Calculate completion time
        if (todo.completed_at) {
          const createdDate = new Date(todo.created_at);
          const completedDate = new Date(todo.completed_at);
          const completionDays = differenceInDays(completedDate, createdDate);
          totalCompletionTime += completionDays;
        }
        
        // Check if on time or late
        if (todo.deadline && todo.completed_at) {
          const deadlineDate = new Date(todo.deadline);
          const completedDate = new Date(todo.completed_at);
          const daysDiff = differenceInDays(deadlineDate, completedDate);
          
          if (formatDateToString(completedDate) <= formatDateToString(deadlineDate)) {
            result.onTimeTasks++;
            totalDaysEarly += daysDiff;
          } else {
            result.lateTasks++;
            totalDaysLate += Math.abs(daysDiff);
          }
        } else if (!todo.deadline) {
          // Tasks without deadline count as on-time when completed
          result.onTimeTasks++;
        }
      } else if (todo.status === 'cancelled') {
        result.cancelledTasks++;
      } else {
        result.pendingTasks++;
        
        // Check if overdue
        if (todo.deadline) {
          const deadlineDate = new Date(todo.deadline);
          const today = new Date();
          if (today > deadlineDate) {
            result.overdueTasks++;
          }
        }
      }
    });

    // Calculate averages
    if (result.onTimeTasks > 0) {
      result.averageDaysEarly = Math.round(totalDaysEarly / result.onTimeTasks);
    }
    if (result.lateTasks > 0) {
      result.averageDaysLate = Math.round(totalDaysLate / result.lateTasks);
    }
    if (result.completedTasks > 0) {
      result.averageCompletionTime = Math.round(totalCompletionTime / result.completedTasks);
      result.successRate = Math.round((result.onTimeTasks / result.completedTasks) * 100);
    }
    
    // Calculate task velocity (tasks completed per week)
    const daysDiff = differenceInDays(dateRange.to, dateRange.from) || 1;
    const weeks = daysDiff / 7;
    result.taskVelocity = Math.round((result.completedTasks / weeks) * 10) / 10; // Round to 1 decimal

    return result;
  }, [filteredTodos, dateRange]);

  // Calculate task type distribution (Routine vs Ad-hoc)
  const taskTypeMetrics = useMemo(() => {
    const routine = {
      total: 0,
      completed: 0,
      onTime: 0,
      late: 0,
      percentage: 0,
      averageDuration: 0,
      totalEstimatedMinutes: 0,
      totalActualMinutes: 0,
      timePercentage: 0,
      averageEstimatedMinutes: 0,
      averageActualMinutes: 0,
      tasks: [] as StaffTodo[]
    };
    const adHoc = {
      total: 0,
      completed: 0,
      onTime: 0,
      late: 0,
      percentage: 0,
      averageDuration: 0,
      totalEstimatedMinutes: 0,
      totalActualMinutes: 0,
      timePercentage: 0,
      averageEstimatedMinutes: 0,
      averageActualMinutes: 0,
      tasks: [] as StaffTodo[]
    };

    let routineDurationTotal = 0;
    let adHocDurationTotal = 0;

    filteredTodos.forEach(todo => {
      const isRoutine = todo.task_type === 'Routine';
      const target = isRoutine ? routine : adHoc;

      target.total++;
      target.tasks.push(todo);

      // Accumulate estimated time (in minutes)
      if (todo.estimated_duration) {
        target.totalEstimatedMinutes += todo.estimated_duration;
      }

      // Accumulate actual time (in minutes) for completed tasks
      if (todo.actual_duration) {
        target.totalActualMinutes += todo.actual_duration;
      }

      if (todo.status === 'completed') {
        target.completed++;

        // Calculate completion time
        if (todo.completed_at) {
          const createdDate = new Date(todo.created_at);
          const completedDate = new Date(todo.completed_at);
          const days = differenceInDays(completedDate, createdDate);
          if (isRoutine) {
            routineDurationTotal += days;
          } else {
            adHocDurationTotal += days;
          }
        }

        // Check if on time or late
        if (todo.deadline && todo.completed_at) {
          const deadlineDate = new Date(todo.deadline);
          deadlineDate.setHours(0, 0, 0, 0);
          const completedDate = new Date(todo.completed_at);
          completedDate.setHours(0, 0, 0, 0);

          if (completedDate <= deadlineDate) {
            target.onTime++;
          } else {
            target.late++;
          }
        } else if (!todo.deadline) {
          target.onTime++;
        }
      }
    });

    // Calculate percentages and averages
    const totalTasks = routine.total + adHoc.total;
    if (totalTasks > 0) {
      routine.percentage = Math.round((routine.total / totalTasks) * 100);
      adHoc.percentage = Math.round((adHoc.total / totalTasks) * 100);
    }

    // Calculate time-based percentages
    const totalEstimatedTime = routine.totalEstimatedMinutes + adHoc.totalEstimatedMinutes;
    if (totalEstimatedTime > 0) {
      routine.timePercentage = Math.round((routine.totalEstimatedMinutes / totalEstimatedTime) * 100);
      adHoc.timePercentage = Math.round((adHoc.totalEstimatedMinutes / totalEstimatedTime) * 100);
    }

    // Calculate average durations
    if (routine.completed > 0) {
      routine.averageDuration = Math.round(routineDurationTotal / routine.completed);
    }
    if (adHoc.completed > 0) {
      adHoc.averageDuration = Math.round(adHocDurationTotal / adHoc.completed);
    }

    // Calculate average estimated and actual time per task
    if (routine.total > 0) {
      routine.averageEstimatedMinutes = Math.round(routine.totalEstimatedMinutes / routine.total);
    }
    if (adHoc.total > 0) {
      adHoc.averageEstimatedMinutes = Math.round(adHoc.totalEstimatedMinutes / adHoc.total);
    }
    if (routine.completed > 0) {
      routine.averageActualMinutes = Math.round(routine.totalActualMinutes / routine.completed);
    }
    if (adHoc.completed > 0) {
      adHoc.averageActualMinutes = Math.round(adHoc.totalActualMinutes / adHoc.completed);
    }

    return { routine, adHoc };
  }, [filteredTodos]);

  // Calculate performance by category
  const categoryPerformance = useMemo((): CategoryPerformance[] => {
    const categoryMap = new Map<string, CategoryPerformance>();
    
    // Initialize all categories
    TASK_CATEGORIES.forEach(category => {
      categoryMap.set(category, {
        category,
        total: 0,
        completed: 0,
        onTime: 0,
        late: 0,
        successRate: 0,
        averageCompletionTime: 0
      });
    });

    // Add uncategorized
    categoryMap.set('Uncategorized', {
      category: 'Uncategorized',
      total: 0,
      completed: 0,
      onTime: 0,
      late: 0,
      successRate: 0,
      averageCompletionTime: 0
    });

    // Process todos
    filteredTodos.forEach(todo => {
      const category = todo.category || 'Uncategorized';
      const perf = categoryMap.get(category) || categoryMap.get('Uncategorized')!;
      
      perf.total++;
      
      if (todo.status === 'completed') {
        perf.completed++;
        
        if (todo.deadline && todo.completed_at) {
          const deadlineDate = new Date(todo.deadline);
          const completedDate = new Date(todo.completed_at);
          
          if (completedDate <= deadlineDate) {
            perf.onTime++;
          } else {
            perf.late++;
          }
        } else if (!todo.deadline) {
          perf.onTime++;
        }
        
        // Calculate completion time
        if (todo.completed_at) {
          const createdDate = new Date(todo.created_at);
          const completedDate = new Date(todo.completed_at);
          const days = differenceInDays(completedDate, createdDate);
          perf.averageCompletionTime += days;
        }
      }
    });

    // Calculate success rates and averages
    categoryMap.forEach(perf => {
      if (perf.completed > 0) {
        perf.successRate = Math.round((perf.onTime / perf.completed) * 100);
        perf.averageCompletionTime = Math.round(perf.averageCompletionTime / perf.completed);
      }
    });

    // Filter out categories with no tasks and sort by total tasks
    return Array.from(categoryMap.values())
      .filter(perf => perf.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredTodos]);

  // Calculate performance by priority
  const priorityPerformance = useMemo((): PriorityPerformance[] => {
    const priorityMap = new Map<string, PriorityPerformance>();
    
    ['urgent', 'high', 'medium', 'low'].forEach(priority => {
      priorityMap.set(priority, {
        priority,
        total: 0,
        completed: 0,
        onTime: 0,
        late: 0,
        successRate: 0
      });
    });

    filteredTodos.forEach(todo => {
      const perf = priorityMap.get(todo.priority)!;
      perf.total++;
      
      if (todo.status === 'completed') {
        perf.completed++;
        
        if (todo.deadline && todo.completed_at) {
          const deadlineDate = new Date(todo.deadline);
          const completedDate = new Date(todo.completed_at);
          
          if (completedDate <= deadlineDate) {
            perf.onTime++;
          } else {
            perf.late++;
          }
        } else if (!todo.deadline) {
          perf.onTime++;
        }
      }
    });

    // Calculate success rates
    priorityMap.forEach(perf => {
      if (perf.completed > 0) {
        perf.successRate = Math.round((perf.onTime / perf.completed) * 100);
      }
    });

    return Array.from(priorityMap.values()).filter(perf => perf.total > 0);
  }, [filteredTodos]);

  // Identify performance insights
  const insights = useMemo(() => {
    const insights: string[] = [];
    
    // Best performing category
    const bestCategory = categoryPerformance
      .filter(c => c.completed >= 3) // At least 3 completed tasks
      .sort((a, b) => b.successRate - a.successRate)[0];
    if (bestCategory && bestCategory.successRate >= 80) {
      insights.push(`Performs best with ${bestCategory.category} tasks (${bestCategory.successRate}% on-time)`);
    }
    
    // Worst performing category
    const worstCategory = categoryPerformance
      .filter(c => c.completed >= 3)
      .sort((a, b) => a.successRate - b.successRate)[0];
    if (worstCategory && worstCategory.successRate < 60) {
      insights.push(`Needs improvement in ${worstCategory.category} (${worstCategory.successRate}% on-time)`);
    }
    
    // Overdue tasks warning
    if (metrics.overdueTasks > 0) {
      insights.push(`${metrics.overdueTasks} task${metrics.overdueTasks > 1 ? 's' : ''} currently overdue`);
    }
    
    // High performer recognition
    if (metrics.successRate >= 90 && metrics.completedTasks >= 10) {
      insights.push('Consistently high performer with 90%+ success rate');
    }
    
    // Task velocity insight
    if (metrics.taskVelocity > 5) {
      insights.push(`High productivity: ${metrics.taskVelocity} tasks/week`);
    } else if (metrics.taskVelocity < 2 && metrics.totalTasks > 5) {
      insights.push(`Low completion rate: ${metrics.taskVelocity} tasks/week`);
    }
    
    return insights;
  }, [categoryPerformance, metrics]);

  // Helper functions for time formatting
  const minutesToHours = (minutes: number): number => {
    return Math.round(minutes / 60 * 10) / 10; // Round to 1 decimal place
  };

  const formatHours = (minutes: number): string => {
    const hours = minutesToHours(minutes);
    return `${hours}h`;
  };

  const calculateTimeEfficiency = (actualMinutes: number, estimatedMinutes: number): number => {
    if (estimatedMinutes === 0) return 0;
    return Math.round((actualMinutes / estimatedMinutes) * 100);
  };

  // Export function
  const handleExport = () => {
    const escapeCsvField = (value: string | number) => {
      const stringValue = String(value);
      return /[",\r\n]/.test(stringValue)
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue;
    };

    const csvHeaders = [
      'Metric',
      'Value'
    ];

    const csvData = [
      ['Staff Name', staffName],
      ['Date Range', `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`],
      ['', ''],
      ['Performance Metrics', ''],
      ['Total Tasks', metrics.totalTasks],
      ['Completed Tasks', metrics.completedTasks],
      ['On-Time Tasks', metrics.onTimeTasks],
      ['Late Tasks', metrics.lateTasks],
      ['Pending Tasks', metrics.pendingTasks],
      ['Overdue Tasks', metrics.overdueTasks],
      ['Success Rate', `${metrics.successRate}%`],
      ['Average Days Early', metrics.averageDaysEarly],
      ['Average Days Late', metrics.averageDaysLate],
      ['Task Velocity', `${metrics.taskVelocity} tasks/week`],
      ['', ''],
      ['Category Performance', ''],
      ...categoryPerformance.map(cat => [
        cat.category,
        `${cat.total} total, ${cat.successRate}% on-time`
      ])
    ];

    const csvContent = [
      csvHeaders.map(escapeCsvField).join(','),
      ...csvData.map(row => row.map(escapeCsvField).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `performance-review-${staffName.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export Successful',
      description: `Performance review exported for ${staffName}`,
    });
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-purple-100 text-purple-700';
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle className="text-2xl">Performance Review - {staffName}</DialogTitle>
            <div className="flex gap-2">
              <Select value={dateRangeOption} onValueChange={(value: DateRangeOption) => setDateRangeOption(value)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7days">Last 7 Days</SelectItem>
                  <SelectItem value="30days">Last 30 Days</SelectItem>
                  <SelectItem value="90days">Last 90 Days</SelectItem>
                  <SelectItem value="180days">Last 6 Months</SelectItem>
                  <SelectItem value="365days">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              
              {dateRangeOption === 'custom' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateRange.from && customDateRange.to
                        ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
                        : 'Select dates'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={(range) => range?.from && range?.to && setCustomDateRange({ from: range.from, to: range.to })}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              )}
              
              <Button onClick={handleExport} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  Success Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-xl font-bold ${getSuccessRateColor(metrics.successRate)}`}>
                  {metrics.successRate}%
                </div>
                <p className="text-xs text-muted-foreground">On-time completion</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-green-600" />
                  Completed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold">{metrics.completedTasks}</div>
                <p className="text-xs text-muted-foreground">Of {metrics.totalTasks} total</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium flex items-center gap-1">
                  <Clock className="h-3 w-3 text-blue-600" />
                  Velocity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold">{metrics.taskVelocity}</div>
                <p className="text-xs text-muted-foreground">Tasks/week</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  Avg Early
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold">{metrics.averageDaysEarly}d</div>
                <p className="text-xs text-muted-foreground">Before deadline</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-orange-600" />
                  Avg Late
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold">{metrics.averageDaysLate}d</div>
                <p className="text-xs text-muted-foreground">After deadline</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 text-red-600" />
                  Overdue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold text-red-600">{metrics.overdueTasks}</div>
                <p className="text-xs text-muted-foreground">Need attention</p>
              </CardContent>
            </Card>
          </div>

          {/* Performance Insights */}
          {insights.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Performance Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.map((insight, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Badge variant="outline" className="h-5 w-5 rounded-full p-0 flex items-center justify-center">
                        {insight.includes('high performer') ? <Award className="h-3 w-3" /> :
                         insight.includes('overdue') || insight.includes('improvement') ? <AlertTriangle className="h-3 w-3" /> :
                         <Activity className="h-3 w-3" />}
                      </Badge>
                      <span className="text-sm">{insight}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabbed Content */}
          <Tabs defaultValue="category" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="category">By Category</TabsTrigger>
              <TabsTrigger value="priority">By Priority</TabsTrigger>
              <TabsTrigger value="tasktype">Task Distribution</TabsTrigger>
              <TabsTrigger value="recent">Recent Tasks</TabsTrigger>
            </TabsList>

            <TabsContent value="category" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Performance by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Completed</TableHead>
                        <TableHead className="text-center">On Time</TableHead>
                        <TableHead className="text-center">Late</TableHead>
                        <TableHead className="text-center">Success Rate</TableHead>
                        <TableHead className="text-center">Avg Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryPerformance.map(cat => (
                        <TableRow key={cat.category}>
                          <TableCell className="font-medium">{cat.category}</TableCell>
                          <TableCell className="text-center">{cat.total}</TableCell>
                          <TableCell className="text-center">{cat.completed}</TableCell>
                          <TableCell className="text-center text-green-600">{cat.onTime}</TableCell>
                          <TableCell className="text-center text-orange-600">{cat.late}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Progress value={cat.successRate} className="w-16" />
                              <span className={`font-bold ${getSuccessRateColor(cat.successRate)}`}>
                                {cat.successRate}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {cat.averageCompletionTime > 0 ? `${cat.averageCompletionTime}d` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="priority" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <PieChartIcon className="h-4 w-4" />
                    Performance by Priority
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Priority</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Completed</TableHead>
                        <TableHead className="text-center">On Time</TableHead>
                        <TableHead className="text-center">Late</TableHead>
                        <TableHead className="text-center">Success Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priorityPerformance.map(priority => (
                        <TableRow key={priority.priority}>
                          <TableCell>
                            <Badge className={getPriorityBadgeColor(priority.priority)}>
                              {priority.priority.charAt(0).toUpperCase() + priority.priority.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{priority.total}</TableCell>
                          <TableCell className="text-center">{priority.completed}</TableCell>
                          <TableCell className="text-center text-green-600">{priority.onTime}</TableCell>
                          <TableCell className="text-center text-orange-600">{priority.late}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Progress value={priority.successRate} className="w-16" />
                              <span className={`font-bold ${getSuccessRateColor(priority.successRate)}`}>
                                {priority.successRate}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tasktype" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <PieChartIcon className="h-4 w-4" />
                    Task Type Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* View Mode Toggle */}
                    <div className="flex justify-center gap-2">
                      <Button
                        variant={chartViewMode === 'count' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartViewMode('count')}
                      >
                        By Task Count
                      </Button>
                      <Button
                        variant={chartViewMode === 'estimated' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartViewMode('estimated')}
                      >
                        By Estimated Time
                      </Button>
                      <Button
                        variant={chartViewMode === 'actual' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartViewMode('actual')}
                        disabled={taskTypeMetrics.routine.totalActualMinutes === 0 && taskTypeMetrics.adHoc.totalActualMinutes === 0}
                      >
                        By Actual Time
                      </Button>
                    </div>

                    {/* Pie Chart */}
                    <div className="w-full h-[300px] md:h-[400px]">
                      <ChartContainer
                        config={{
                          routine: {
                            label: "Routine",
                            color: "#3b82f6",
                          },
                          adHoc: {
                            label: "Ad-hoc",
                            color: "#f97316",
                          },
                        }}
                      >
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: 'Routine',
                                value: chartViewMode === 'count'
                                  ? taskTypeMetrics.routine.total
                                  : chartViewMode === 'estimated'
                                    ? taskTypeMetrics.routine.totalEstimatedMinutes / 60 // Convert to hours
                                    : taskTypeMetrics.routine.totalActualMinutes / 60,
                                fill: '#3b82f6'
                              },
                              {
                                name: 'Ad-hoc',
                                value: chartViewMode === 'count'
                                  ? taskTypeMetrics.adHoc.total
                                  : chartViewMode === 'estimated'
                                    ? taskTypeMetrics.adHoc.totalEstimatedMinutes / 60 // Convert to hours
                                    : taskTypeMetrics.adHoc.totalActualMinutes / 60,
                                fill: '#f97316'
                              }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => {
                              const percentage = (percent * 100).toFixed(0);
                              return `${name}: ${percentage}%`;
                            }}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                            onClick={(data) => {
                              setSelectedTaskType(data.name as 'Routine' | 'Ad-hoc');
                            }}
                            cursor="pointer"
                          >
                            <Cell key="routine" fill="#3b82f6" />
                            <Cell key="adHoc" fill="#f97316" />
                          </Pie>
                          <ChartTooltip
                            content={({ active, payload }) => {
                              if (!active || !payload || payload.length === 0) return null;

                              const data = payload[0];
                              const isRoutine = data.name === 'Routine';
                              const metrics = isRoutine ? taskTypeMetrics.routine : taskTypeMetrics.adHoc;

                              // Sort tasks by time consumption based on view mode
                              const sortedTasks = [...metrics.tasks];
                              if (chartViewMode === 'estimated') {
                                sortedTasks.sort((a, b) => (b.estimated_duration || 0) - (a.estimated_duration || 0));
                              } else if (chartViewMode === 'actual') {
                                sortedTasks.sort((a, b) => (b.actual_duration || 0) - (a.actual_duration || 0));
                              }
                              // For count mode, keep original order

                              const tasks = sortedTasks; // Show ALL tasks

                              const fillColor = (data.payload as Record<string, unknown>)?.fill as string || '#888';
                              return (
                                <div className="rounded-lg border bg-background p-3 shadow-xl max-w-[350px]">
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between border-b pb-2">
                                      <span className="font-semibold text-sm">{data.name} Tasks</span>
                                      <Badge variant="outline" style={{ backgroundColor: fillColor, color: 'white', borderColor: fillColor }}>
                                        {metrics.percentage}%
                                      </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      <div>
                                        <span className="text-muted-foreground">Total:</span>
                                        <span className="ml-1 font-medium">{metrics.total}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Completed:</span>
                                        <span className="ml-1 font-medium">{metrics.completed}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">On-time:</span>
                                        <span className="ml-1 font-medium text-green-600">{metrics.onTime}</span>
                                      </div>
                                      <div>
                                        <span className="text-muted-foreground">Late:</span>
                                        <span className="ml-1 font-medium text-orange-600">{metrics.late}</span>
                                      </div>
                                    </div>

                                    {/* Time Allocation Section */}
                                    {chartViewMode !== 'count' && (
                                      <div className="border-t pt-2">
                                        <div className="text-xs font-medium mb-1.5">Time Allocation:</div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                          {chartViewMode === 'estimated' && (
                                            <>
                                              <div>
                                                <span className="text-muted-foreground">Total Estimated:</span>
                                                <span className="ml-1 font-medium">{formatHours(metrics.totalEstimatedMinutes)}</span>
                                              </div>
                                              <div>
                                                <span className="text-muted-foreground">Avg per Task:</span>
                                                <span className="ml-1 font-medium">{formatHours(metrics.averageEstimatedMinutes)}</span>
                                              </div>
                                            </>
                                          )}
                                          {chartViewMode === 'actual' && (
                                            <>
                                              <div>
                                                <span className="text-muted-foreground">Total Actual:</span>
                                                <span className="ml-1 font-medium">{formatHours(metrics.totalActualMinutes)}</span>
                                              </div>
                                              <div>
                                                <span className="text-muted-foreground">Avg per Task:</span>
                                                <span className="ml-1 font-medium">{formatHours(metrics.averageActualMinutes)}</span>
                                              </div>
                                              {metrics.totalEstimatedMinutes > 0 && (
                                                <div className="col-span-2">
                                                  <span className="text-muted-foreground">Time Efficiency:</span>
                                                  <span className={`ml-1 font-medium ${
                                                    calculateTimeEfficiency(metrics.totalActualMinutes, metrics.totalEstimatedMinutes) <= 110
                                                      ? 'text-green-600'
                                                      : 'text-orange-600'
                                                  }`}>
                                                    {calculateTimeEfficiency(metrics.totalActualMinutes, metrics.totalEstimatedMinutes)}%
                                                  </span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {tasks.length > 0 && (
                                      <>
                                        <div className="border-t pt-2">
                                          <div className="text-xs font-medium mb-1.5">
                                            {chartViewMode === 'count' ? 'All Tasks:' : 'Tasks by Time (Most to Least):'}
                                          </div>
                                          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                                            {tasks.map(task => {
                                              const isLate = task.deadline && task.completed_at &&
                                                new Date(task.completed_at) > new Date(task.deadline);
                                              const isCompleted = task.status === 'completed';
                                              const isPending = !isCompleted;

                                              return (
                                                <div key={task.id} className="text-xs space-y-0.5">
                                                  <div className="flex items-start gap-1">
                                                    <span className={`mt-0.5 ${isCompleted ? 'text-green-600' : isPending ? 'text-yellow-600' : 'text-gray-400'}`}>
                                                      {isCompleted ? '✓' : isPending ? '⏱' : '○'}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                      <div className="font-medium truncate">{task.title}</div>
                                                      <div className="flex items-center gap-1.5 flex-wrap">
                                                        {task.category && (
                                                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                                            {task.category}
                                                          </Badge>
                                                        )}
                                                        {task.deadline && (
                                                          <span className={`text-[10px] ${isLate ? 'text-red-600' : 'text-muted-foreground'}`}>
                                                            {isCompleted && isLate ? '(Late)' : `Due: ${format(new Date(task.deadline), 'MMM d')}`}
                                                          </span>
                                                        )}
                                                        {isCompleted && !isLate && (
                                                          <span className="text-[10px] text-green-600">(On time)</span>
                                                        )}
                                                        {/* Time Information */}
                                                        {chartViewMode !== 'count' && (
                                                          <>
                                                            {task.estimated_duration && chartViewMode === 'estimated' && (
                                                              <span className="text-[10px] text-blue-600">
                                                                Est: {formatHours(task.estimated_duration)}
                                                              </span>
                                                            )}
                                                            {task.actual_duration && chartViewMode === 'actual' && (
                                                              <span className="text-[10px] text-purple-600">
                                                                Actual: {formatHours(task.actual_duration)}
                                                              </span>
                                                            )}
                                                            {chartViewMode === 'actual' && task.estimated_duration && task.actual_duration && (
                                                              <span className={`text-[10px] font-medium ${
                                                                calculateTimeEfficiency(task.actual_duration, task.estimated_duration) <= 110
                                                                  ? 'text-green-600'
                                                                  : 'text-orange-600'
                                                              }`}>
                                                                ({calculateTimeEfficiency(task.actual_duration, task.estimated_duration)}%)
                                                              </span>
                                                            )}
                                                          </>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </PieChart>
                      </ChartContainer>
                      <div className="text-center text-xs text-muted-foreground mt-2">
                        Click on a segment to view all tasks
                      </div>
                    </div>

                    {/* Persistent Task List Panel */}
                    {selectedTaskType && (
                      <Card className="mt-4">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${selectedTaskType === 'Routine' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
                              {selectedTaskType} Tasks
                              <span className="text-muted-foreground font-normal">
                                ({chartViewMode === 'count' ? 'All Tasks' : 'Sorted by Time'})
                              </span>
                            </CardTitle>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedTaskType(null)}
                            >
                              ✕
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {(() => {
                              const metrics = selectedTaskType === 'Routine' ? taskTypeMetrics.routine : taskTypeMetrics.adHoc;
                              const sortedTasks = [...metrics.tasks];
                              if (chartViewMode === 'estimated') {
                                sortedTasks.sort((a, b) => (b.estimated_duration || 0) - (a.estimated_duration || 0));
                              } else if (chartViewMode === 'actual') {
                                sortedTasks.sort((a, b) => (b.actual_duration || 0) - (a.actual_duration || 0));
                              }

                              return sortedTasks.map(task => {
                                const isLate = task.deadline && task.completed_at &&
                                  new Date(task.completed_at) > new Date(task.deadline);
                                const isCompleted = task.status === 'completed';

                                return (
                                  <div key={task.id} className="p-2 border rounded-lg hover:bg-muted/50">
                                    <div className="flex items-start gap-2">
                                      <span className={`mt-0.5 ${isCompleted ? 'text-green-600' : 'text-yellow-600'}`}>
                                        {isCompleted ? '✓' : '⏱'}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">{task.title}</div>
                                        <div className="flex items-center gap-2 flex-wrap mt-1">
                                          {task.category && (
                                            <Badge variant="outline" className="text-xs">
                                              {task.category}
                                            </Badge>
                                          )}
                                          {task.priority && (
                                            <Badge variant="outline" className="text-xs">
                                              {task.priority}
                                            </Badge>
                                          )}
                                          {task.deadline && (
                                            <span className={`text-xs ${isLate ? 'text-red-600' : 'text-muted-foreground'}`}>
                                              {isCompleted && isLate ? 'Late' : `Due: ${format(new Date(task.deadline), 'MMM d')}`}
                                            </span>
                                          )}
                                          {isCompleted && !isLate && (
                                            <span className="text-xs text-green-600">On time</span>
                                          )}
                                          {chartViewMode === 'estimated' && task.estimated_duration && (
                                            <span className="text-xs font-medium text-blue-600">
                                              Est: {formatHours(task.estimated_duration)}
                                            </span>
                                          )}
                                          {chartViewMode === 'actual' && task.actual_duration && (
                                            <span className="text-xs font-medium text-purple-600">
                                              Actual: {formatHours(task.actual_duration)}
                                            </span>
                                          )}
                                          {chartViewMode === 'actual' && task.estimated_duration && task.actual_duration && (
                                            <span className={`text-xs font-medium ${
                                              calculateTimeEfficiency(task.actual_duration, task.estimated_duration) <= 110
                                                ? 'text-green-600'
                                                : 'text-orange-600'
                                            }`}>
                                              {calculateTimeEfficiency(task.actual_duration, task.estimated_duration)}% efficiency
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Routine Card */}
                      <Card className="border-t-4 border-t-blue-500">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                              Routine Tasks
                            </span>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {taskTypeMetrics.routine.percentage}%
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Total Tasks</span>
                              <span className="font-medium">{taskTypeMetrics.routine.total}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Completed</span>
                              <span className="font-medium">{taskTypeMetrics.routine.completed} / {taskTypeMetrics.routine.total}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">On-time Rate</span>
                              <span className="font-medium text-green-600">
                                {taskTypeMetrics.routine.completed > 0
                                  ? Math.round((taskTypeMetrics.routine.onTime / taskTypeMetrics.routine.completed) * 100)
                                  : 0}%
                              </span>
                            </div>
                            {taskTypeMetrics.routine.averageDuration > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Avg Completion</span>
                                <span className="font-medium">{taskTypeMetrics.routine.averageDuration}d</span>
                              </div>
                            )}
                            {/* Time Metrics */}
                            {taskTypeMetrics.routine.totalEstimatedMinutes > 0 && (
                              <>
                                <div className="flex justify-between text-sm pt-2 border-t">
                                  <span className="text-muted-foreground">Est. Time</span>
                                  <span className="font-medium text-blue-600">
                                    {formatHours(taskTypeMetrics.routine.totalEstimatedMinutes)}
                                  </span>
                                </div>
                                {taskTypeMetrics.routine.total > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Avg Est./Task</span>
                                    <span className="font-medium">{formatHours(taskTypeMetrics.routine.averageEstimatedMinutes)}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {taskTypeMetrics.routine.totalActualMinutes > 0 && (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Actual Time</span>
                                  <span className="font-medium text-purple-600">
                                    {formatHours(taskTypeMetrics.routine.totalActualMinutes)}
                                  </span>
                                </div>
                                {taskTypeMetrics.routine.completed > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Avg Actual/Task</span>
                                    <span className="font-medium">{formatHours(taskTypeMetrics.routine.averageActualMinutes)}</span>
                                  </div>
                                )}
                                {taskTypeMetrics.routine.totalEstimatedMinutes > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Time Efficiency</span>
                                    <span className={`font-medium ${
                                      calculateTimeEfficiency(taskTypeMetrics.routine.totalActualMinutes, taskTypeMetrics.routine.totalEstimatedMinutes) <= 110
                                        ? 'text-green-600'
                                        : 'text-orange-600'
                                    }`}>
                                      {calculateTimeEfficiency(taskTypeMetrics.routine.totalActualMinutes, taskTypeMetrics.routine.totalEstimatedMinutes)}%
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <Progress
                            value={taskTypeMetrics.routine.completed > 0
                              ? (taskTypeMetrics.routine.completed / taskTypeMetrics.routine.total) * 100
                              : 0}
                            className="h-2"
                          />
                          {/* Time Progress Bar */}
                          {taskTypeMetrics.routine.totalEstimatedMinutes > 0 && taskTypeMetrics.routine.totalActualMinutes > 0 && (
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Time Progress</span>
                                <span>{calculateTimeEfficiency(taskTypeMetrics.routine.totalActualMinutes, taskTypeMetrics.routine.totalEstimatedMinutes)}%</span>
                              </div>
                              <Progress
                                value={Math.min(
                                  (taskTypeMetrics.routine.totalActualMinutes / taskTypeMetrics.routine.totalEstimatedMinutes) * 100,
                                  100
                                )}
                                className="h-2"
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Ad-hoc Card */}
                      <Card className="border-t-4 border-t-orange-500">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                              Ad-hoc Tasks
                            </span>
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              {taskTypeMetrics.adHoc.percentage}%
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Total Tasks</span>
                              <span className="font-medium">{taskTypeMetrics.adHoc.total}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Completed</span>
                              <span className="font-medium">{taskTypeMetrics.adHoc.completed} / {taskTypeMetrics.adHoc.total}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">On-time Rate</span>
                              <span className="font-medium text-green-600">
                                {taskTypeMetrics.adHoc.completed > 0
                                  ? Math.round((taskTypeMetrics.adHoc.onTime / taskTypeMetrics.adHoc.completed) * 100)
                                  : 0}%
                              </span>
                            </div>
                            {taskTypeMetrics.adHoc.averageDuration > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Avg Completion</span>
                                <span className="font-medium">{taskTypeMetrics.adHoc.averageDuration}d</span>
                              </div>
                            )}
                            {/* Time Metrics */}
                            {taskTypeMetrics.adHoc.totalEstimatedMinutes > 0 && (
                              <>
                                <div className="flex justify-between text-sm pt-2 border-t">
                                  <span className="text-muted-foreground">Est. Time</span>
                                  <span className="font-medium text-blue-600">
                                    {formatHours(taskTypeMetrics.adHoc.totalEstimatedMinutes)}
                                  </span>
                                </div>
                                {taskTypeMetrics.adHoc.total > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Avg Est./Task</span>
                                    <span className="font-medium">{formatHours(taskTypeMetrics.adHoc.averageEstimatedMinutes)}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {taskTypeMetrics.adHoc.totalActualMinutes > 0 && (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Actual Time</span>
                                  <span className="font-medium text-purple-600">
                                    {formatHours(taskTypeMetrics.adHoc.totalActualMinutes)}
                                  </span>
                                </div>
                                {taskTypeMetrics.adHoc.completed > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Avg Actual/Task</span>
                                    <span className="font-medium">{formatHours(taskTypeMetrics.adHoc.averageActualMinutes)}</span>
                                  </div>
                                )}
                                {taskTypeMetrics.adHoc.totalEstimatedMinutes > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Time Efficiency</span>
                                    <span className={`font-medium ${
                                      calculateTimeEfficiency(taskTypeMetrics.adHoc.totalActualMinutes, taskTypeMetrics.adHoc.totalEstimatedMinutes) <= 110
                                        ? 'text-green-600'
                                        : 'text-orange-600'
                                    }`}>
                                      {calculateTimeEfficiency(taskTypeMetrics.adHoc.totalActualMinutes, taskTypeMetrics.adHoc.totalEstimatedMinutes)}%
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <Progress
                            value={taskTypeMetrics.adHoc.completed > 0
                              ? (taskTypeMetrics.adHoc.completed / taskTypeMetrics.adHoc.total) * 100
                              : 0}
                            className="h-2"
                          />
                          {/* Time Progress Bar */}
                          {taskTypeMetrics.adHoc.totalEstimatedMinutes > 0 && taskTypeMetrics.adHoc.totalActualMinutes > 0 && (
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Time Progress</span>
                                <span>{calculateTimeEfficiency(taskTypeMetrics.adHoc.totalActualMinutes, taskTypeMetrics.adHoc.totalEstimatedMinutes)}%</span>
                              </div>
                              <Progress
                                value={Math.min(
                                  (taskTypeMetrics.adHoc.totalActualMinutes / taskTypeMetrics.adHoc.totalEstimatedMinutes) * 100,
                                  100
                                )}
                                className="h-2"
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="recent" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Recent Tasks (Last 10)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Deadline</TableHead>
                        <TableHead>Completed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTodos.slice(0, 10).map(todo => (
                        <TableRow key={todo.id}>
                          <TableCell className="font-medium max-w-xs truncate">{todo.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {todo.category || 'Uncategorized'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getPriorityBadgeColor(todo.priority)}>
                              {todo.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={todo.status === 'completed' ? 'default' : 'secondary'}>
                              {todo.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {todo.deadline ? format(new Date(todo.deadline), 'MMM d') : '-'}
                          </TableCell>
                          <TableCell>
                            {todo.completed_at ? format(new Date(todo.completed_at), 'MMM d') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaffPerformanceReview;
